import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  CheckCircle,
  XCircle,
  Clock,
  User,
  Calendar,
  FileText,
  MessageSquare,
  AlertCircle,
  Search,
  Filter,
  Eye,
  Loader2
} from 'lucide-react';
import { leavesApi, LeaveApplication } from '../../services/api/leaves';
import { useAuthStore } from '../auth/authStore';
import toast from 'react-hot-toast';

interface LeaveApprovalProps {
  userRole: 'MANAGER' | 'FIRM_ADMIN';
}

const LeaveApproval: React.FC<LeaveApprovalProps> = ({ userRole }) => {
  const { user } = useAuthStore();
  const [applications, setApplications] = useState<LeaveApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<LeaveApplication | null>(null);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject' | null>(null);
  const [comments, setComments] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [filters, setFilters] = useState({
    status: 'PENDING',
    search: '',
    leaveType: '',
  });

  // Fetch pending applications
  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await leavesApi.getLeaveApplications({
        status: filters.status ? filters.status.toLowerCase() : undefined,
        sortBy: 'appliedAt',
        sortOrder: 'desc',
      });





      // Filter applications that need approval at current user's level
      const filteredApps = response.applications.filter(app => {
        if (app.status !== 'PENDING') return filters.status !== 'PENDING';

        const currentLevel = app.approvalWorkflow.currentLevel;
        const currentLevelInfo = app.approvalWorkflow.levels.find(level => level.level === currentLevel);

        if (!currentLevelInfo) return false;

        // Check if current user's role matches the required approver role
        // Handle case mismatch: backend stores 'FirmAdmin'/'Manager', frontend passes 'FIRM_ADMIN'/'MANAGER'
        const normalizedUserRole = userRole === 'FIRM_ADMIN' ? 'FirmAdmin' : 'Manager';
        const roleMatches = currentLevelInfo.approverRole === normalizedUserRole;

        // For firm admin, if role matches, show the application
        // The backend already filters by tenant, so role matching should be sufficient
        return roleMatches;
      });

      setApplications(filteredApps);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to load leave applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [filters.status, userRole]);

  // Handle approval/rejection
  const handleApproval = async () => {
    if (!selectedApplication || !approvalAction) return;
    
    if (approvalAction === 'reject' && !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    try {
      setProcessing(selectedApplication.id);
      
      await leavesApi.processLeaveApproval(selectedApplication.id, {
        action: approvalAction,
        comments: comments.trim() || undefined,
        rejectionReason: approvalAction === 'reject' ? rejectionReason.trim() : undefined,
      });
      
      toast.success(`Leave application ${approvalAction}d successfully`);
      
      // Reset form and close dialog
      setSelectedApplication(null);
      setApprovalAction(null);
      setComments('');
      setRejectionReason('');
      
      // Refresh applications
      await fetchApplications();
    } catch (error: any) {
      console.error('Error processing approval:', error);
      const errorMessage = error.response?.data?.message || `Failed to ${approvalAction} leave application`;
      toast.error(errorMessage);
    } finally {
      setProcessing(null);
    }
  };

  // Filter applications based on search
  const filteredApplications = applications.filter(app => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch = 
        app.reason.toLowerCase().includes(searchLower) ||
        app.leaveType?.name.toLowerCase().includes(searchLower) ||
        app.user?.firstName.toLowerCase().includes(searchLower) ||
        app.user?.lastName.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    
    if (filters.leaveType && app.leaveTypeId !== filters.leaveType) return false;
    
    return true;
  });

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending Approval</Badge>;
      case 'APPROVED':
        return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
      case 'REJECTED':
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Calculate leave duration
  const calculateDuration = (startDate: string, endDate: string, isHalfDay: boolean) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return isHalfDay ? 0.5 : diffDays;
  };

  // Get approval workflow status
  const getWorkflowStatus = (application: LeaveApplication) => {
    const { currentLevel, levels } = application.approvalWorkflow;
    const currentLevelInfo = levels.find(level => level.level === currentLevel);



    // Allow both Manager and FirmAdmin to approve leaves
    // Handle role name variations between frontend and backend
    const normalizeRole = (role: string) => {
      if (role === 'FIRM_ADMIN' || role === 'FirmAdmin') return 'FIRM_ADMIN';
      if (role === 'MANAGER' || role === 'Manager') return 'MANAGER';
      return role;
    };

    const normalizedApproverRole = normalizeRole(currentLevelInfo?.approverRole || '');
    const normalizedUserRole = normalizeRole(userRole);

    // Allow either Manager or FirmAdmin to approve any pending leave
    const canApprove = normalizedApproverRole === normalizedUserRole ||
                      (normalizedApproverRole === 'FIRM_ADMIN' && normalizedUserRole === 'MANAGER') ||
                      (normalizedApproverRole === 'MANAGER' && normalizedUserRole === 'FIRM_ADMIN');



    return {
      currentLevel,
      totalLevels: levels.length,
      currentApprover: currentLevelInfo?.approverRole,
      isMyTurn: canApprove,
    };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Leave Approvals</h1>
          <p className="text-muted-foreground">
            Review and approve leave applications from your team
          </p>
        </div>
        <Button onClick={fetchApplications} disabled={loading} variant="outline">
          <Loader2 className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search applications..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="pl-10"
                />
              </div>
            </div>
            <Select
              value={filters.status}
              onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              placeholder="Status"
            >
              <SelectTrigger className="w-[150px]">
                <SelectContent>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="APPROVED">Approved</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                  <SelectItem value="">All Status</SelectItem>
                </SelectContent>
              </SelectTrigger>
            </Select>
            <Button
              variant="outline"
              onClick={() => setFilters({ status: 'PENDING', search: '', leaveType: '' })}
            >
              <Filter className="h-4 w-4 mr-2" />
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Applications List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Leave Applications</span>
            <Badge variant="secondary">{filteredApplications.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span>Loading applications...</span>
            </div>
          ) : filteredApplications.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No leave applications found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredApplications.map((application) => {
                const workflowStatus = getWorkflowStatus(application);
                
                return (
                  <div
                    key={application.id}
                    className={`border rounded-lg p-4 transition-colors ${
                      workflowStatus.isMyTurn ? 'border-blue-200 bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="flex items-center space-x-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="font-semibold">
                              {application.user?.firstName} {application.user?.lastName}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: application.leaveType?.color || '#gray' }}
                            />
                            <span className="font-medium">{application.leaveType?.name}</span>
                          </div>
                          {getStatusBadge(application.status)}
                          {workflowStatus.isMyTurn && (
                            <Badge className="bg-blue-100 text-blue-800">
                              Awaiting Your Approval
                            </Badge>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm mb-3">
                          <div>
                            <span className="text-muted-foreground">Duration:</span>
                            <div className="font-medium">
                              {formatDate(application.startDate)} - {formatDate(application.endDate)}
                              <span className="ml-2 text-muted-foreground">
                                ({calculateDuration(application.startDate, application.endDate, application.isHalfDay)} days)
                              </span>
                            </div>
                          </div>
                          
                          <div>
                            <span className="text-muted-foreground">Applied:</span>
                            <div className="font-medium">
                              {formatDate(application.appliedAt)}
                            </div>
                          </div>
                          
                          <div>
                            <span className="text-muted-foreground">Approval Level:</span>
                            <div className="font-medium">
                              {workflowStatus.currentLevel} of {workflowStatus.totalLevels}
                            </div>
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <span className="text-muted-foreground text-sm">Reason:</span>
                          <p className="text-sm mt-1">{application.reason}</p>
                        </div>

                        {/* Approval Status Information */}
                        {(() => {
                          const getApprovalStatusInfo = () => {
                            if (application.status === 'PENDING' && application.approvalWorkflow?.levels) {
                              const currentLevel = application.approvalWorkflow.currentLevel;
                              const currentLevelInfo = application.approvalWorkflow.levels.find(level => level.level === currentLevel);

                              if (currentLevelInfo && typeof currentLevelInfo.approverId === 'object') {
                                const approver = currentLevelInfo.approverId;
                                const approverName = `${approver.firstName} ${approver.lastName}`;
                                const approverRole = currentLevelInfo.approverRole === 'FirmAdmin' ? 'Firm Admin' : currentLevelInfo.approverRole;

                                return (
                                  <div className="mb-3 p-2 bg-yellow-50 rounded border-l-4 border-yellow-200">
                                    <span className="text-yellow-800 text-sm font-medium">Pending with:</span>
                                    <p className="text-yellow-700 text-sm mt-1">{approverName} ({approverRole})</p>
                                  </div>
                                );
                              }
                            } else if (application.status === 'APPROVED' && application.approvalWorkflow?.levels) {
                              // Find the last approved level
                              const approvedLevels = application.approvalWorkflow.levels.filter(level => level.status === 'approved');
                              const lastApprovedLevel = approvedLevels[approvedLevels.length - 1];

                              if (lastApprovedLevel && typeof lastApprovedLevel.approverId === 'object') {
                                const approver = lastApprovedLevel.approverId;
                                const approverName = `${approver.firstName} ${approver.lastName}`;
                                const approverRole = lastApprovedLevel.approverRole === 'FirmAdmin' ? 'Firm Admin' : lastApprovedLevel.approverRole;

                                return (
                                  <div className="mb-3 p-2 bg-green-50 rounded border-l-4 border-green-200">
                                    <span className="text-green-800 text-sm font-medium">Approved by:</span>
                                    <p className="text-green-700 text-sm mt-1">{approverName} ({approverRole})</p>
                                  </div>
                                );
                              }
                            }
                            return null;
                          };

                          return getApprovalStatusInfo();
                        })()}

                        {application.rejectionReason && (
                          <div className="mb-3 p-2 bg-red-50 rounded border-l-4 border-red-200">
                            <span className="text-red-800 text-sm font-medium">Rejection Reason:</span>
                            <p className="text-red-700 text-sm mt-1">{application.rejectionReason}</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex space-x-2">
                        {workflowStatus.isMyTurn && application.status === 'PENDING' && (
                          <>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setSelectedApplication(application);
                                    setApprovalAction('approve');
                                  }}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Approve
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Approve Leave Application</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">
                                      Comments (Optional)
                                    </label>
                                    <Textarea
                                      placeholder="Add any comments for the employee..."
                                      value={comments}
                                      onChange={(e) => setComments(e.target.value)}
                                      rows={3}
                                    />
                                  </div>
                                  <div className="flex justify-end space-x-2">
                                    <Button
                                      variant="outline"
                                      onClick={() => {
                                        setSelectedApplication(null);
                                        setApprovalAction(null);
                                        setComments('');
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      onClick={handleApproval}
                                      disabled={processing === application.id}
                                      className="bg-green-600 hover:bg-green-700"
                                    >
                                      {processing === application.id ? (
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                      ) : (
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                      )}
                                      Approve
                                    </Button>
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>

                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => {
                                    setSelectedApplication(application);
                                    setApprovalAction('reject');
                                  }}
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Reject
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Reject Leave Application</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">
                                      Rejection Reason *
                                    </label>
                                    <Textarea
                                      placeholder="Please provide a reason for rejection..."
                                      value={rejectionReason}
                                      onChange={(e) => setRejectionReason(e.target.value)}
                                      rows={3}
                                    />
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">
                                      Additional Comments (Optional)
                                    </label>
                                    <Textarea
                                      placeholder="Add any additional comments..."
                                      value={comments}
                                      onChange={(e) => setComments(e.target.value)}
                                      rows={2}
                                    />
                                  </div>
                                  <div className="flex justify-end space-x-2">
                                    <Button
                                      variant="outline"
                                      onClick={() => {
                                        setSelectedApplication(null);
                                        setApprovalAction(null);
                                        setComments('');
                                        setRejectionReason('');
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      onClick={handleApproval}
                                      disabled={processing === application.id}
                                      variant="destructive"
                                    >
                                      {processing === application.id ? (
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                      ) : (
                                        <XCircle className="h-4 w-4 mr-2" />
                                      )}
                                      Reject
                                    </Button>
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </>
                        )}
                        
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LeaveApproval;
